import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, CheckCircle2, ImagePlus, Loader2, type LucideIcon } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { MAX_FOTO_LAUDO_BYTES } from "@shared/laudo-midia";
import { POSICOES_VEICULO, ROTULO_POSICAO_VEICULO, posicaoOpcionalVeiculo, type PosicaoVeiculo } from "@shared/veiculo-fotos";
import type { VeiculoFoto } from "@shared/schema";
import { api } from "@shared/routes";

const TIPOS = ["image/jpeg", "image/png", "image/webp"];
const LEGENDA_LADO: Partial<Record<PosicaoVeiculo, string>> = {
  lateral_esquerda: "Lado esquerdo do veículo",
  lateral_direita: "Lado direito do veículo",
};
const FOCO = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const GRADE = "grid grid-cols-2 gap-3 sm:grid-cols-3";
const ehNavegadorMobile = () => typeof navigator !== "undefined" &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
export const chaveFotosVeiculo = (veiculoId: string) => ["/api/veiculos", veiculoId, "fotos"] as const;
const nomePosicao = (p: PosicaoVeiculo) => ROTULO_POSICAO_VEICULO[p].toLowerCase();

export async function buscarFotosVeiculo(veiculoId: string, token: string | undefined): Promise<VeiculoFoto[]> {
  const r = await fetch(`/api/veiculos/${veiculoId}/fotos`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error("Não foi possível carregar as fotos do veículo.");
  return r.json();
}

/** Fotos do veículo. A grade, o bloqueio da atribuição e o modal de solicitar laudo compartilham a mesma chave de cache. */
export function useFotosVeiculo(veiculoId: string | null | undefined, enabled = true) {
  const { session } = useAuth();
  return useQuery<VeiculoFoto[]>({
    queryKey: chaveFotosVeiculo(veiculoId ?? ""),
    enabled: enabled && !!veiculoId && !!session,
    queryFn: () => buscarFotosVeiculo(veiculoId!, session?.access_token),
  });
}

/** Link para nova aba ou, quando quem usa já tem um lightbox, botão que chama `onAmpliar`. */
function Ampliar({ url, nome, onAmpliar, className, children }: {
  url: string; nome: string; onAmpliar?: (url: string) => void; className?: string; children: ReactNode;
}) {
  const rotulo = `Ampliar foto ${nome} do veículo`;
  return onAmpliar
    ? <button type="button" aria-label={rotulo} onClick={() => onAmpliar(url)} className={cn(FOCO, className)}>{children}</button>
    : <a href={url} target="_blank" rel="noopener noreferrer" aria-label={rotulo} className={cn(FOCO, className)}>{children}</a>;
}

/** Foto cadastrada. Se a imagem não abrir, o registro continua e a pessoa pode tentar carregar de novo. */
function ImagemFoto({ url, nome, ampliar }: { url: string; nome: string; ampliar: boolean }) {
  const [falhou, setFalhou] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  if (falhou) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center text-xs text-muted-foreground">
        <span>Não foi possível abrir a foto.</span>
        <Button type="button" size="sm" variant="outline" onClick={() => { setFalhou(false); setTentativa((t) => t + 1); }}>Tentar de novo</Button>
      </div>
    );
  }
  const imagem = <img key={tentativa} src={url} alt={`Foto ${nome} do veículo`} className="h-full w-full object-cover" onError={() => setFalhou(true)} />;
  return ampliar ? <Ampliar url={url} nome={nome} className="block h-full w-full">{imagem}</Ampliar> : imagem;
}

/** Botão de arquivo: `label` com o `input` sr-only dentro (label não aceita `disabled`, então desabilitamos o input e o label). */
function BotaoArquivo({ texto, nome, Icone, variant, camera = false, desabilitado, onArquivo }: {
  texto: string; nome: string; Icone: LucideIcon; variant: "secondary" | "outline"; camera?: boolean;
  desabilitado: boolean; onArquivo: (file: File | undefined) => void;
}) {
  return (
    <label
      aria-disabled={desabilitado || undefined}
      className={cn(
        buttonVariants({ variant, size: "sm" }),
        "min-h-11 w-full cursor-pointer focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        desabilitado && "pointer-events-none opacity-50",
      )}
    >
      <Icone aria-hidden />
      {texto}
      <input
        type="file" className="sr-only" aria-label={nome} disabled={desabilitado}
        accept={camera ? "image/*" : TIPOS.join(",")} capture={camera ? "environment" : undefined}
        onChange={(e) => { onArquivo(e.target.files?.[0]); e.target.value = ""; }}
      />
    </label>
  );
}

export function FotosVeiculo({ veiculoId, somenteLeitura = false, onChange, onEnviandoChange }: {
  veiculoId: string; somenteLeitura?: boolean; onChange?: (fotos: VeiculoFoto[]) => void;
  /** Avisa quem usa a grade quando um envio começa e termina (por exemplo, para travar o botão do rodapé). */
  onEnviandoChange?: (enviando: boolean) => void;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [enviando, setEnviando] = useState<PosicaoVeiculo | null>(null);
  const [falhou, setFalhou] = useState<PosicaoVeiculo | null>(null);
  const mobile = ehNavegadorMobile();
  const chave = chaveFotosVeiculo(veiculoId);
  const { data, isPending, isError, refetch } = useFotosVeiculo(veiculoId);
  const fotos = data ?? [];

  async function enviar(posicao: PosicaoVeiculo, file: File | undefined) {
    if (!file || enviando) return;
    setFalhou(null);
    const recusa = !TIPOS.includes(file.type)
      ? { title: "Formato não aceito", description: "Selecione uma foto válida em JPG, PNG ou WebP." }
      : file.size > MAX_FOTO_LAUDO_BYTES ? { title: "Foto muito grande", description: "A foto deve ter até 10 MB." } : null;
    if (recusa) {
      setFalhou(posicao);
      toast({ variant: "destructive", ...recusa });
      return;
    }
    setEnviando(posicao);
    onEnviandoChange?.(true);
    try {
      const r = await fetch(`/api/veiculos/${veiculoId}/fotos/${posicao}`, {
        method: "POST", headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": file.type }, body: file,
      }).catch(() => null); // sem rede: mensagem em português em vez do erro técnico do navegador
      if (!r) throw new Error("Não foi possível salvar a foto.");
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(r.status === 413 ? "A foto deve ter até 10 MB." : corpo.message ?? "Não foi possível salvar a foto.");
      if (!corpo.fotoUrl) throw new Error("Não foi possível salvar a foto."); // 200 sem JSON válido não pode virar foto quebrada
      const novas = qc.setQueryData<VeiculoFoto[]>(chave, (atuais = []) => [...atuais.filter((f) => f.posicao !== posicao), corpo as VeiculoFoto]);
      // O detalhe contém um snapshot de veiculo.fotos. Invalidar só a grade deixa
      // esse laudo antigo no cache; a quarta foto também pode atribuir o TV no banco.
      void qc.invalidateQueries({ queryKey: [api.laudos.get.path] });
      void qc.invalidateQueries({ queryKey: [api.laudos.list.path] });
      void qc.invalidateQueries({ queryKey: [api.laudos.stats.path] });
      onChange?.(novas ?? []);
    } catch (e) {
      setFalhou(posicao);
      toast({ variant: "destructive", title: "Erro ao enviar foto", description: (e as Error).message });
    } finally {
      setEnviando(null);
      onEnviandoChange?.(false);
    }
  }

  if (isPending) {
    return (
      <div role="status">
        <span className="sr-only">Carregando fotos do veículo…</span>
        <div className={GRADE}>{POSICOES_VEICULO.map((p) => <Skeleton key={p} className="aspect-[4/3] rounded-md" />)}</div>
      </div>
    );
  }
  if (isError && !data) {
    return (
      <div className="space-y-2">
        <p role="alert" className="text-sm text-destructive">Não foi possível carregar as fotos do veículo.</p>
        <Button type="button" size="sm" variant="outline" className="min-h-11" onClick={() => void refetch()}>Tentar de novo</Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">Considere os lados olhando no sentido da frente do veículo.</p>
      <div className={GRADE}>
        {POSICOES_VEICULO.map((posicao) => {
          const foto = fotos.find((f) => f.posicao === posicao);
          const nome = nomePosicao(posicao);
          const esteEnviando = enviando === posicao;
          return (
            <figure key={posicao} className="flex min-w-0 flex-col gap-2">
              <div className={cn(
                "relative aspect-[4/3] overflow-hidden rounded-md",
                foto ? "border bg-muted" : somenteLeitura ? "border bg-muted/40" : "border-2 border-dashed bg-muted/40",
                falhou === posicao && "border-destructive",
              )}>
                <div className={cn("h-full w-full", esteEnviando && "opacity-50")}>
                  {foto
                    ? <ImagemFoto key={foto.fotoUrl} url={foto.fotoUrl} nome={nome} ampliar={somenteLeitura} />
                    : (
                      <div className="flex h-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground">
                        {!somenteLeitura && <Camera className="h-5 w-5" aria-hidden />}
                        Sem foto
                      </div>
                    )}
                </div>
                {esteEnviando && (
                  <div role="status" className="absolute inset-0 flex items-center justify-center gap-1 text-xs font-medium">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />Enviando…
                  </div>
                )}
              </div>
              <figcaption className="flex flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1 text-sm font-medium">
                  <span>{ROTULO_POSICAO_VEICULO[posicao]}</span>
                  {posicaoOpcionalVeiculo(posicao) && <span className="text-xs font-normal text-muted-foreground">Opcional</span>}
                  {foto && (<><CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden /><span className="sr-only">Foto registrada</span></>)}
                </span>
                {LEGENDA_LADO[posicao] && <span className="text-xs text-muted-foreground">{LEGENDA_LADO[posicao]}</span>}
                {falhou === posicao && <span role="alert" className="text-xs text-destructive">Não foi enviada. Tente de novo.</span>}
              </figcaption>
              {!somenteLeitura && (
                <div className="flex flex-col gap-2">
                  {mobile && (
                    <BotaoArquivo camera variant="secondary" Icone={Camera} texto="Tirar foto" nome={`Tirar foto ${nome}`}
                      desabilitado={!!enviando} onArquivo={(f) => void enviar(posicao, f)} />
                  )}
                  <BotaoArquivo variant="outline" Icone={ImagePlus} texto={foto ? "Trocar foto" : "Enviar foto"}
                    nome={`${foto ? "Trocar" : "Enviar"} foto ${nome}`} desabilitado={!!enviando} onArquivo={(f) => void enviar(posicao, f)} />
                </div>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  );
}

export type ArquivosFotosVeiculo = Partial<Record<PosicaoVeiculo, File>>;

/** Escolhe as fotos antes de existir um cadastro; quem salva controla os envios e as tentativas. */
export function FotosVeiculoRascunho({ arquivos, salvas, desabilitado, onArquivo }: {
  arquivos: ArquivosFotosVeiculo;
  salvas: Partial<Record<PosicaoVeiculo, VeiculoFoto>>;
  desabilitado: boolean;
  onArquivo: (posicao: PosicaoVeiculo, arquivo: File | undefined) => void;
}) {
  const { toast } = useToast();
  const [previews, setPreviews] = useState<Partial<Record<PosicaoVeiculo, string>>>({});
  useEffect(() => {
    const urls: Partial<Record<PosicaoVeiculo, string>> = {};
    for (const posicao of POSICOES_VEICULO) {
      const file = arquivos[posicao];
      if (file) urls[posicao] = URL.createObjectURL(file);
    }
    setPreviews(urls);
    return () => { for (const url of Object.values(urls)) URL.revokeObjectURL(url); };
  }, [arquivos]);

  const escolher = (posicao: PosicaoVeiculo, file: File | undefined) => {
    if (!file || desabilitado) return;
    if (!TIPOS.includes(file.type) || file.size > MAX_FOTO_LAUDO_BYTES) {
      toast({ variant: "destructive", title: "Foto inválida", description: "Selecione JPG, PNG ou WebP de até 10 MB." });
      return;
    }
    onArquivo(posicao, file);
  };
  const mobile = ehNavegadorMobile();
  return <div className="space-y-2">
    <p className="text-xs text-muted-foreground">Selecione as fotos agora. Elas serão enviadas ao salvar o veículo.</p>
    <p className="text-xs text-muted-foreground">Considere os lados olhando no sentido da frente do veículo.</p>
    <div className={GRADE}>{POSICOES_VEICULO.map((posicao) => {
      const nome = nomePosicao(posicao);
      const url = previews[posicao] ?? salvas[posicao]?.fotoUrl;
      return <figure key={posicao} className="flex min-w-0 flex-col gap-2">
        <div className={cn("relative aspect-[4/3] overflow-hidden rounded-md bg-muted/40", url ? "border" : "border-2 border-dashed")}>
          {url ? <ImagemFoto key={url} url={url} nome={nome} ampliar={false} /> :
            <div className="flex h-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground"><Camera className="h-5 w-5" aria-hidden />Sem foto</div>}
        </div>
        <figcaption className="text-sm font-medium">{ROTULO_POSICAO_VEICULO[posicao]}
          {posicaoOpcionalVeiculo(posicao) && <span className="ml-1 text-xs font-normal text-muted-foreground">Opcional</span>}
          <span className="block text-xs font-normal text-muted-foreground">{arquivos[posicao] ? "Selecionada para envio" : salvas[posicao] ? "Foto registrada" : LEGENDA_LADO[posicao]}</span>
        </figcaption>
        {mobile && <BotaoArquivo camera variant="secondary" Icone={Camera} texto="Tirar foto" nome={`Tirar foto ${nome}`}
          desabilitado={desabilitado} onArquivo={(f) => escolher(posicao, f)} />}
        <BotaoArquivo variant="outline" Icone={ImagePlus} texto={url ? "Trocar foto" : "Enviar foto"} nome={`${url ? "Trocar" : "Enviar"} foto ${nome}`}
          desabilitado={desabilitado} onArquivo={(f) => escolher(posicao, f)} />
        {arquivos[posicao] && <Button type="button" size="sm" variant="ghost" disabled={desabilitado}
          onClick={() => onArquivo(posicao, undefined)} aria-label={`Remover seleção da foto ${nome}`}>Remover seleção</Button>}
      </figure>;
    })}</div>
  </div>;
}

/** Miniaturas lado a lado que quebram linha (topo do laudo). Devolve `null` sem fotos, então o título nunca aparece sozinho. */
export function MiniaturasVeiculo({ fotos, onAmpliar }: {
  fotos: Pick<VeiculoFoto, "posicao" | "fotoUrl">[]; onAmpliar?: (url: string) => void;
}) {
  const presentes = POSICOES_VEICULO.flatMap((posicao) => {
    const foto = fotos.find((f) => f.posicao === posicao);
    return foto ? [{ posicao, fotoUrl: foto.fotoUrl }] : [];
  });
  if (!presentes.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Fotos do veículo</h3>
      <div className="flex flex-wrap gap-2">
        {presentes.map(({ posicao, fotoUrl }) => (
          <figure key={posicao} className="flex flex-col gap-1">
            <Ampliar url={fotoUrl} nome={nomePosicao(posicao)} onAmpliar={onAmpliar} className="block rounded-md">
              <img src={fotoUrl} alt={`Foto ${nomePosicao(posicao)} do veículo`} className="h-20 w-28 rounded-md border object-cover" />
            </Ampliar>
            <figcaption className="text-xs text-muted-foreground">{ROTULO_POSICAO_VEICULO[posicao]}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
